import React, { useEffect, useMemo, useRef, useState } from "react";
import WideContainer from "../components/WideContainer";
import CircularProgress from "@mui/material/CircularProgress";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import { Trans, useTranslation } from "react-i18next";

import UserBar from "../components/UserBar";
import axios from "../components/Axios";
import { withCdnCookie } from "../app/cdnCookie";
import QueryBar, { ALL_FOLDERS } from "../components/search/QueryBar";
import ResultGroups from "../components/search/ResultGroups";
import MomentPreview from "../components/search/MomentPreview";
import { findMentions, groupResults, localizeResults, previewConcepts, searchEstimate, searchLang } from "../components/search/nlqModel";
import { trackDrag } from "./videos/scenes/drag";
import "../components/search/Search.css";

const NO_RESULTS = [];
const NO_VIDEOS = {};
// the result list is 400px (Search.css) until the user drags the grip; a dragged list stays between LIST_MIN and what
// leaves the preview PREVIEW_MIN
const LIST_MIN = 300,
  PREVIEW_MIN = 420,
  GAP = 14;

const userFolder = () => JSON.parse(localStorage.getItem("user") || "{}").user_folder;

// the search is kept in the address (?q=&in=) so that Back from a result's video comes back to it; its response is
// kept for the tab's session, so coming back doesn't wait for the search again
const CACHE = "search:last";
const cacheKey = (q, inFolder, lang) => JSON.stringify([q, inFolder, lang]);
function cached(key) {
  try {
    const c = JSON.parse(sessionStorage.getItem(CACHE) || "null");
    return c && c.key === key ? c : null;
  } catch (e) {
    return null;
  }
}
function saveCache(key, data, took) {
  try {
    sessionStorage.setItem(CACHE, JSON.stringify({ key, data, took }));
  } catch (e) {
    // too big for sessionStorage: coming back searches again
  }
}

// Search all videos with one sentence: @mentions for people, "quotes" for exact words. Results are grouped by
// video and scene, with the selected moment previewed on the right.
export default function Search({ history, location }) {
  const { t, i18n } = useTranslation();
  const linked = useRef(new URLSearchParams(location?.search || "")); // the search in the address when the page opened
  const [text, setText] = useState(() => linked.current.get("q") || "");
  const [committed, setCommitted] = useState(""); // query of the shown results
  const [folder, setFolder] = useState(() => linked.current.get("in") || ALL_FOLDERS);
  const [folders, setFolders] = useState([]);
  const [people, setPeople] = useState([]);
  const [peopleFor, setPeopleFor] = useState(null); // folder whose people have loaded
  const [data, setData] = useState(null); // /search/nlq response
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false); // false, "failed" (try again) or "invalid" (the API refused the query)
  const [sort, setSort] = useState("relevance");
  const [showRelated, setShowRelated] = useState(false);
  const [selected, setSelected] = useState(null);
  const [hovered, setHovered] = useState(null);
  const [took, setTook] = useState(null); // ms the shown results took
  const request = useRef(0); // only the latest search updates the results
  const started = useRef(0);
  const [listW, setListW] = useState(null); // dragged result list width, null = default
  const [resizing, setResizing] = useState(false);
  const stopDrag = useRef(null);
  useEffect(() => () => stopDrag.current?.(), []);

  const userId = localStorage.getItem("user_id");
  useEffect(() => {
    if (userId === null) {
      history.push("/login");
      return;
    }
    axios
      .get("/user/" + userId + "/video_projects", { params: { folder: "/" } })
      // in the library's order
      .then((response) => setFolders((response.data.folders || []).slice().sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }))))
      .catch((error) => console.log(error));
  }, [userId, history]);

  // people that can be mentioned: those in the folder's videos
  useEffect(() => {
    if (userId === null) return;
    let current = true;
    axios
      .post("/search/options", { search_folders: folder === ALL_FOLDERS ? [] : [folder] })
      .then((response) => {
        if (!current) return;
        setPeople(response.data.filter((o) => o.object === "Characters").map((o) => ({ name: o.value, videos: o.videos || 0 })));
        setPeopleFor(folder);
      })
      .catch((error) => {
        console.log(error);
        if (current) setPeopleFor(folder);
      });
    return () => {
      current = false;
    };
  }, [userId, folder]);

  const names = useMemo(() => people.map((p) => p.name), [people]);
  // everyone met in any folder: a query's @mentions stay mentions when it runs in another folder, whose people
  // haven't loaded yet
  const known = useRef(new Set());
  names.forEach((n) => known.current.add(n));

  const search = (query, inFolder = folder) => {
    const q = query.trim();
    setCommitted(q);
    setSelected(null);
    setShowRelated(false);
    setError(false);
    setTook(null);
    const id = ++request.current;
    // keep the search in the address, for Back and for sharing
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (q && inFolder !== ALL_FOLDERS) params.set("in", inFolder);
    const address = "/search" + (params.toString() ? "?" + params : "");
    if (history && history.location.pathname + history.location.search !== address) history.replace(address);
    if (!q) {
      setData(null);
      setLoading(false);
      return;
    }
    const lang = searchLang(i18n.language);
    const key = cacheKey(q, inFolder, lang);
    const hit = cached(key);
    if (hit) {
      setData(hit.data);
      setTook(hit.took);
      setLoading(false);
      return;
    }
    setLoading(true);
    started.current = performance.now();
    // results preview CDN videos: show them once its cookie is set
    withCdnCookie(
      axios.post("/search/nlq", {
        query: q,
        folders: inFolder === ALL_FOLDERS ? [] : [inFolder],
        mentions: [...new Set(findMentions(q, [...known.current]).map((m) => m.name))],
        lang,
      })
    )
      .then((response) => {
        if (id !== request.current) return;
        const ms = performance.now() - started.current;
        setData(response.data);
        setTook(ms);
        setLoading(false);
        saveCache(key, response.data, ms);
      })
      .catch((e) => {
        console.log(e);
        if (id !== request.current) return;
        setData(null);
        setLoading(false);
        // 400: the API refused the query itself (e.g. too long), searching again won't help
        setError(e && e.response && e.response.status === 400 ? "invalid" : "failed");
      });
  };

  // the search in the address when the page opened (Back from a result, a shared link): at once when its results
  // are kept, else once the folder's people are known, so that its @mentions are understood
  const linkRan = useRef(false);
  useEffect(() => {
    const q = (linked.current.get("q") || "").trim();
    if (!q || linkRan.current) return;
    const inFolder = linked.current.get("in") || ALL_FOLDERS;
    if (peopleFor === null && !cached(cacheKey(q, inFolder, searchLang(i18n.language)))) return;
    linkRan.current = true;
    search(q, inFolder);
  }, [peopleFor]); // eslint-disable-line react-hooks/exhaustive-deps

  // another folder searches again, for what the box shows (what was searched, if the box was cleared)
  const changeFolder = (f) => {
    setFolder(f);
    if (committed) search(text.trim() || committed, f);
  };

  // results come in English and French: switching the language shows them in it without searching again (the
  // reasons stay in the language of the search)
  const results = useMemo(() => localizeResults(data?.results || NO_RESULTS, i18n.language), [data, i18n.language]);
  const videos = data?.videos || NO_VIDEOS;
  const { groups, order, counts, relatedCount } = useMemo(() => groupResults(results, videos, { sort, showRelated }), [results, videos, sort, showRelated]);
  const byId = useMemo(() => Object.fromEntries(results.map((r) => [r.id, r])), [results]);
  const current = byId[order.includes(selected) ? selected : order[0]];
  const index = current ? order.indexOf(current.id) : -1;
  const shown = useMemo(() => new Set(order), [order]);

  const dirty = text.trim() !== committed && !!text.trim();
  const concepts = !dirty && data && committed === text.trim() ? data.understood : previewConcepts(text, names);
  const folderName = folder === ALL_FOLDERS ? t("all folders") : folder;
  const estimate = loading ? searchEstimate(committed, names) : text.trim() ? null : searchEstimate("", names);

  const folderOf = (projectId) => userFolder() + "/" + videos[projectId]?.project_folder;
  const videoSrc = (projectId) => folderOf(projectId) + "/" + videos[projectId]?.video_file;
  const thumbSrc = (projectId) => folderOf(projectId) + "/video_lowres_thumbnail.jpg";
  const count = (n, one, many) => (n === 1 ? t(one) : t(many, { count: n }));
  const seconds = (ms) => (ms / 1000).toLocaleString(i18n.language, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  const segmented = (value, options, set) => (
    <div className="sp-seg" role="group">
      {options.map(([v, label]) => (
        <button key={String(v)} type="button" aria-pressed={value === v} onClick={() => set(v)}>
          {label}
        </button>
      ))}
    </div>
  );

  let summary = "";
  if (data && order.length) {
    const moments = count(counts.moments, "1 moment", "{{count}} moments");
    const inVideos = count(counts.videos, "1 video", "{{count}} videos");
    summary = counts.scenes
      ? t("{{moments}} in {{scenes}}, {{videos}}", { moments, scenes: count(counts.scenes, "1 scene", "{{count}} scenes"), videos: inVideos })
      : t("{{moments}} in {{videos}}", { moments, videos: inVideos });
  } else if (data && relatedCount) {
    summary = t("No exact matches");
  }

  // result list width between LIST_MIN and what leaves the preview PREVIEW_MIN in box (.sp-results)
  const clampList = (w, box) => Math.round(Math.max(LIST_MIN, Math.min(box.clientWidth - GAP - PREVIEW_MIN, w)));
  const listWidth = (grip) => grip.nextElementSibling.offsetWidth; // the list follows the grip
  const startResize = (e) => {
    e.preventDefault();
    const grip = e.currentTarget,
      box = grip.parentElement,
      x0 = e.clientX,
      w0 = listWidth(grip);
    setResizing(true);
    stopDrag.current = trackDrag((ev) => setListW(clampList(w0 + ev.clientX - x0, box)), "col-resize", () => setResizing(false));
  };
  const resizeKey = (e) => {
    const step = { ArrowLeft: -24, ArrowRight: 24 }[e.key];
    if (!step) return;
    e.preventDefault();
    setListW(clampList(listWidth(e.currentTarget) + step, e.currentTarget.parentElement));
  };

  let empty = null;
  if (error === "invalid") {
    empty = [t("This search can’t be run"), t("Try rephrasing it with fewer words.")];
  } else if (error) {
    empty = [t("The search didn’t work"), t("Try again in a moment.")];
  } else if (!data) {
    empty = [t("Describe the moment you’re looking for"), t("Mention people with @, then add what they say or what’s on screen.")];
  } else if (!order.length) {
    empty = relatedCount
      ? [t("No moment in {{folder}} matches every part", { folder: folderName }), t("Some moments match part of the sentence.")]
      : [
          t("Nothing in {{folder}} matches this", { folder: folderName }),
          folder === ALL_FOLDERS ? t("Try fewer words, or someone else.") : t("Try fewer words, someone else, or search all folders."),
        ];
  }

  return (
    <>
      <UserBar wide themed title="Search" subtitle={<Trans>Find any moment by what’s said, who’s on screen or what’s seen</Trans>} />
      <div className="searchPage">
        <WideContainer className="sp-body">
          <QueryBar
            text={text}
            onText={setText}
            onSubmit={() => search(text)}
            people={people}
            folder={folder}
            folders={folders}
            onFolder={changeFolder}
            concepts={concepts}
            dirty={dirty}
            estimate={estimate}
            busy={loading}
          />
          <div className="sp-toolbar" role="status" aria-live="polite">
            <span className="sp-summary">
              {loading ? t("Searching…") : summary}
              {!loading && took !== null && (summary ? " · " + t("took {{seconds}} s", { seconds: seconds(took) }) : t("Took {{seconds}} s", { seconds: seconds(took) }))}
            </span>
            {!loading && data && results.length > 0 && (
              <>
                <span className="sp-control">
                  <span className="sp-caption">{t("Sort")}</span>
                  {segmented(sort, [["relevance", t("Relevance")], ["order", t("Video order")]], setSort)}
                </span>
                <span className="sp-control">
                  <span className="sp-caption">{t("Show")}</span>
                  {segmented(
                    showRelated,
                    relatedCount ? [[false, t("Matches")], [true, t("+ Related ({{count}})", { count: relatedCount })]] : [[false, t("Matches")]],
                    setShowRelated,
                  )}
                </span>
              </>
            )}
          </div>
          {loading && <CircularProgress className="sp-loading" />}
          {!loading && empty && (
            <div className="sp-empty">
              <SearchOffIcon className="sp-empty-icon" aria-hidden="true" />
              <div className="sp-empty-title">{empty[0]}</div>
              <div className="sp-empty-hint">{empty[1]}</div>
              {data && !order.length && relatedCount > 0 && (
                <button type="button" className="sp-empty-action" onClick={() => setShowRelated(true)}>
                  {count(relatedCount, "Show 1 related moment", "Show {{count}} related moments")}
                </button>
              )}
            </div>
          )}
          {!loading && current && (
            <div className="sp-results" style={listW === null ? undefined : { "--sp-list": listW + "px" }}>
              <div
                className="sp-grip"
                role="separator"
                aria-orientation="vertical"
                aria-label={t("Resize the result list")}
                tabIndex={0}
                data-on={resizing || undefined}
                title={t("Drag to resize columns · double-click to reset")}
                onMouseDown={startResize}
                onDoubleClick={() => setListW(null)}
                onKeyDown={resizeKey}
              />
              <ResultGroups
                groups={groups}
                videos={videos}
                selected={current.id}
                hovered={hovered}
                onSelect={setSelected}
                onHover={setHovered}
                highlight={data.highlight}
                folderOf={folderOf}
                videoSrc={videoSrc}
                thumbSrc={thumbSrc}
              />
              <MomentPreview
                r={current}
                video={videos[current.project_id] || {}}
                pins={results.filter((x) => x.project_id === current.project_id && shown.has(x.id))}
                index={index}
                total={order.length}
                hovered={hovered}
                onSelect={setSelected}
                onStep={(d) => order[index + d] && setSelected(order[index + d])}
                onHover={setHovered}
                highlight={data.highlight}
                videoSrc={videoSrc}
              />
            </div>
          )}
        </WideContainer>
      </div>
    </>
  );
}
